/**
 * PreloadManager.js - Asset Preloading System
 * Gère le chargement des assets avec priorités et feedback de progression
 */
class PreloadManager {
  constructor() {
    this.loaded = 0;
    this.total = 0;
    this.progress = 0;
    this.phase = 'idle'; // idle, loading, complete, error
    this.assets = {
      critical: [], // Chargés en priorité (UI, sons, dos de carte)
      cards: [],    // 10 premières cartes visibles
      deferred: []  // Reste en idle
    };
    this.callbacks = {
      onProgress: null,
      onComplete: null,
      onError: null
    };
  }
  
  /**
   * Configure les callbacks
   */
  on(event, callback) {
    // Accept "progress"/"complete"/"error" OR "onProgress"/"onComplete"/"onError"
    const normalized = event.startsWith('on')
      ? event
      : `on${event.charAt(0).toUpperCase()}${event.slice(1)}`;
    if (Object.prototype.hasOwnProperty.call(this.callbacks, normalized)) {
      this.callbacks[normalized] = callback;
    } else {
      console.warn('[Preload] Unknown event:', event);
    }
  }
  
  /**
   * Ajoute des assets à charger
   */
  addAssets(priority, urls) {
    if (Array.isArray(urls)) {
      this.assets[priority].push(...urls);
    } else {
      this.assets[priority].push(urls);
    }
  }
  
  /**
   * Lance le préchargement
   */
  async start() {
    console.log('[Preload] Starting...');
    this.phase = 'loading';
    this.total = this.assets.critical.length + this.assets.cards.length;
    this.loaded = 0;
    
    console.log(`[Preload] Total assets: ${this.total} (${this.assets.critical.length} critical + ${this.assets.cards.length} cards)`);
    
    try {
      // Phase 1 : Assets critiques (UI + sons)
      console.log('[Preload] Phase 1: Critical assets...');
      await this.loadBatch(this.assets.critical);
      
      // Phase 2 : Premières cartes
      console.log('[Preload] Phase 2: Card assets...');
      await this.loadBatch(this.assets.cards);
      
      // Phase 3 : Reste en idle callback
      console.log('[Preload] Phase 3: Scheduling deferred assets...');
      this.scheduleDeferredLoading();
      
      this.phase = 'complete';
      this.progress = 100;
      
      console.log('[Preload] All phases complete!');
      
      if (this.callbacks.onComplete) {
        this.callbacks.onComplete();
      }
    } catch (error) {
      this.phase = 'error';
      console.error('[Preload] Error:', error);
      
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    }
  }
  
  /**
   * Charge un batch d'assets
   */
  async loadBatch(urls) {
    const promises = urls.map(url => this.loadAsset(url));
    await Promise.allSettled(promises);
  }
  
  /**
   * Charge un asset individuel
   */
  async loadAsset(url) {
    console.log(`[Preload] Loading: ${url}`);
    return new Promise((resolve, reject) => {
      // Détection du type par extension
      const ext = url.split('.').pop().toLowerCase();
      
      if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
        // Image
        const img = new Image();
        img.onload = () => {
          console.log(`[Preload] ✓ Loaded: ${url}`);
          this.updateProgress();
          resolve(url);
        };
        img.onerror = () => {
          console.warn(`[Preload] ✗ Failed to load image: ${url}`);
          this.updateProgress();
          resolve(url); // Resolve anyway to not block
        };
        img.src = url;
      } else if (['mp3', 'ogg', 'wav'].includes(ext)) {
        // Audio
        fetch(url)
          .then(() => {
            console.log(`[Preload] ✓ Loaded: ${url}`);
            this.updateProgress();
            resolve(url);
          })
          .catch(() => {
            console.warn(`[Preload] ✗ Failed to load audio: ${url}`);
            this.updateProgress();
            resolve(url);
          });
      } else {
        // Autre (CSS, JS, etc.)
        fetch(url)
          .then(() => {
            console.log(`[Preload] ✓ Loaded: ${url}`);
            this.updateProgress();
            resolve(url);
          })
          .catch(() => {
            console.warn(`[Preload] ✗ Failed to load: ${url}`);
            this.updateProgress();
            resolve(url);
          });
      }
    });
  }
  
  /**
   * Met à jour la progression
   */
  updateProgress() {
    this.loaded++;
    this.progress = Math.round((this.loaded / this.total) * 100);
    
    if (this.callbacks.onProgress) {
      this.callbacks.onProgress(this.progress, this.loaded, this.total);
    }
  }
  
  /**
   * Charge les assets différés en idle
   */
  scheduleDeferredLoading() {
    if (this.assets.deferred.length === 0) return;
    
    const loadDeferred = () => {
      if (this.assets.deferred.length > 0) {
        const url = this.assets.deferred.shift();
        this.loadAsset(url).then(() => {
          this.scheduleNext();
        });
      }
    };
    
    this.scheduleNext = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadDeferred, { timeout: 2000 });
      } else {
        setTimeout(loadDeferred, 100);
      }
    };
    
    this.scheduleNext();
  }
  
  /**
   * Retourne l'état actuel
   */
  getState() {
    return {
      phase: this.phase,
      progress: this.progress,
      loaded: this.loaded,
      total: this.total
    };
  }
}

// Export global
window.PreloadManager = PreloadManager;
